import { useEffect, useId, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { useTheme } from "../i18n/ThemeContext";
import { useMotionOff, setMotionOff } from "../version/motionPref";

// Einstellungen im Kopf (Version 3): ein quadratisches Feld mit zwei Zahnrädern.
// Beim Klick wächst das Feld nach links und unten auf, die Zahnräder drehen ineinander,
// darunter erscheinen die Schalter für dunklen Modus und Animationen. Platz für weitere Einstellungen ist vorgesehen.

const T = {
    de: { title: "Einstellungen", open: "Einstellungen öffnen", close: "Einstellungen schließen", dark: "Dunkler Modus", motion: "Animationen aus", motionHint: "Bewegungen und Einblendungen stoppen." },
    en: { title: "Settings", open: "Open settings", close: "Close settings", dark: "Dark mode", motion: "Animations off", motionHint: "Stops motion and fade ins." },
};

// Zahnrad als Pfad: n Zähne, Außenradius ro, Fußradius ri, Mittelpunkt cx/cy, Bohrung rh
const gear = (cx, cy, n, ro, ri, rh) => {
    const pts = [];
    for (let i = 0; i < n; i += 1) {
        const a = (i / n) * Math.PI * 2;
        const w = (Math.PI * 2) / n;
        [[a - w * 0.25, ri], [a - w * 0.14, ro], [a + w * 0.14, ro], [a + w * 0.25, ri]].forEach(([t, r]) => pts.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]));
    }
    const outer = `M${pts.map((p) => p.map((v) => v.toFixed(2)).join(",")).join("L")}Z`;
    const hole = `M${(cx + rh).toFixed(2)},${cy}a${rh},${rh} 0 1,0 ${(-2 * rh).toFixed(2)},0a${rh},${rh} 0 1,0 ${(2 * rh).toFixed(2)},0Z`;
    return outer + hole;
};
// Großes Rad 8 Zähne, kleines Rad 6 Zähne, so gesetzt, dass die Zähne ineinandergreifen
const BIG = { cx: 9.5, cy: 10.5, d: gear(0, 0, 8, 6.6, 5, 2.1) };
const SMALL = { cx: 18.6, cy: 5.6, d: gear(0, 0, 6, 4.6, 3.3, 1.5) };

const Gears = ({ turn }) => (
    <svg viewBox="0 0 24 18" width="30" height="23" aria-hidden="true" className="vs-gears">
        <g transform={`translate(${BIG.cx} ${BIG.cy})`}>
            <path d={BIG.d} fillRule="evenodd" className="vs-gear-big" style={{ transform: `rotate(${turn * 135}deg)` }} />
        </g>
        <g transform={`translate(${SMALL.cx} ${SMALL.cy})`}>
            <path d={SMALL.d} fillRule="evenodd" className="vs-gear-small" style={{ transform: `rotate(${15 - turn * 180}deg)` }} />
        </g>
    </svg>
);

const Switch = ({ on, onClick, label, testId }) => (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onClick} data-testid={testId} className={`vs-switch ${on ? "is-on" : ""}`}>
        <i aria-hidden="true" />
    </button>
);

const ViewSettings = () => {
    const { lang } = useLanguage();
    // Eindeutige id je Instanz (Kopf und Burger rendern die Einstellungen zweimal)
    const bodyId = `vs-body-${useId().replace(/:/g, "")}`;
    const { theme, toggleTheme } = useTheme();
    const L = T[lang === "en" ? "en" : "de"];
    const [open, setOpen] = useState(false);
    const motion = useMotionOff();
    // Jeder Klick dreht die Räder ein Stück weiter (auf und zu), statt zurückzuspringen
    const [turn, setTurn] = useState(0);
    const ref = useRef(null);

    const toggle = () => { setOpen((o) => !o); setTurn((t) => t + 1); };
    // Öffnet nur per Klick oder Tastatur, nicht beim Überfahren mit der Maus

    useEffect(() => {
        if (!open) return undefined;
        const close = () => { setOpen(false); setTurn((t) => t + 1); };
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
        const onKey = (e) => { if (e.key === "Escape") close(); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
    }, [open]);

    return (
        <div className={`vs-wrap ${open ? "is-open" : ""}`} ref={ref} data-testid="view-settings">
            <div className="vs-surface">
                <div className="vs-head">
                    <span className="vs-title" aria-hidden={!open}>{L.title}</span>
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={open}
                        aria-controls={bodyId}
                        aria-label={open ? L.close : L.open}
                        title={L.title}
                        data-testid="view-settings-trigger"
                        className="vs-trigger"
                    >
                        <Gears turn={turn} />
                    </button>
                </div>
                <div id={bodyId} className="vs-body" inert={open ? undefined : true} data-testid="view-settings-panel">
                    <div className="vs-body-inner">
                        <div className="vs-row">
                            <span>{L.dark}</span>
                            <Switch on={theme === "dark"} onClick={toggleTheme} label={L.dark} testId="view-settings-dark" />
                        </div>
                        <div className="vs-row">
                            <span>{L.motion}<small>{L.motionHint}</small></span>
                            <Switch on={motion} onClick={() => setMotionOff(!motion)} label={L.motion} testId="view-settings-motion" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewSettings;
